import {
    ChangeDetectionStrategy,
    Component,
    Input,
    ViewEncapsulation,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as React from 'react';

import { CmpsDatePipe } from '../common/date.pipe';
import { CacheDateItem } from '../performance-data/cache-date.pd.model';
import iocTypes from '../rvirus.imap';
import { IocContext } from '../rvirus.ioc';
import { PeriodType } from '../widget/date-range.widget.model';

import { QuickSwitch, QuickSwitchItem } from './config.insight.model';
import { TranslationKeyMap } from './insight.i18n';
import { getEventDateValue, PeriodTypeOpts } from './period.insight.model';

interface SelectionSummaryInsightProps {
    periodType: PeriodType;
    cacheDates: CacheDateItem[];
    eventDate: Date;
    quickSwitch: QuickSwitch;
    quickSwitchItem: QuickSwitchItem;
    quickSwitchLabel: string;
}

function getQuickSwitchLabel(
    quickSwitch: QuickSwitch,
    quickSwitchLabel: string,
    translateService: TranslateService,
): string {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    return (
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        (quickSwitch && quickSwitch.title) ||
        (quickSwitchLabel && translateService.instant(quickSwitchLabel))
    );
}

class SelectionSummaryInsightImpl extends React.Component<SelectionSummaryInsightProps> {
    render(): React.ReactNode {
        const translateService = IocContext.container.get<TranslateService>(
            iocTypes.TranslateService.identifier,
        );
        const datePipe = IocContext.container.get<CmpsDatePipe>(
            iocTypes.CmpsDatePipe.identifier,
        );
        const periodTypeOpt = PeriodTypeOpts.find(
            (e) => e.value === this.props.periodType,
        );

        const periodValue = periodTypeOpt
            ? translateService.instant(periodTypeOpt.tlKey)
            : 'N/A';
        const dateRange = translateService.instant(
            TranslationKeyMap._709_DATE_RANGE,
        );
        const dateRangeValue =
            getEventDateValue(
                this.props.cacheDates,
                this.props.eventDate,
                this.props.periodType,
                datePipe,
            ) || 'N/A';
        const quickSwitch =
            getQuickSwitchLabel(
                this.props.quickSwitch,
                this.props.quickSwitchLabel,
                translateService,
            ) || '';
        const quickSwitchItemValue = this.props.quickSwitchItem
            ? this.props.quickSwitchItem.shortName
            : 'N/A';

        return (
            <div className="insight-selection-summary">
                <div>
                    <span className="insight-snsy-label">Period:</span>{' '}
                    {periodValue}
                </div>
                <div className="insight-snsy-separator" />
                <div>
                    <span className="insight-snsy-label">{dateRange}:</span>{' '}
                    {dateRangeValue}
                </div>
                {quickSwitch && (
                    <>
                        <div className="insight-snsy-separator" />
                        <div>
                            <span className="insight-snsy-label">
                                {quickSwitch}:
                            </span>{' '}
                            {quickSwitchItemValue}
                        </div>
                    </>
                )}
            </div>
        );
    }
}

export const SelectionSummaryInsight = SelectionSummaryInsightImpl;

@Component({
    selector: '[rvirus][cmpsInsightSelectionSummary]',
    template: ` <cmps-insight-selection-summary
        [periodType]="periodType"
        [cacheDates]="cacheDates"
        [eventDate]="eventDate"
        [quickSwitch]="quickSwitch"
        [quickSwitchItem]="quickSwitchItem"
        [quickSwitchLabel]="quickSwitchLabel"
    ></cmps-insight-selection-summary>`,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.ShadowDom,
})
export class SelectionSummaryInsightComponent {
    @Input() periodType: PeriodType;
    @Input() cacheDates: CacheDateItem[];
    @Input() eventDate: Date;
    @Input() quickSwitch: QuickSwitch;
    @Input() quickSwitchItem: QuickSwitchItem;
    @Input() quickSwitchLabel: string;
}
